/**
 * Shared UI utilities for the admin-only Panel de Internal.
 *
 * Deliberately self-contained: StatCard/LoaderBlock/EmptyState y formatters
 * copiados de `components/finops/shared.tsx` en vez de cross-importarlos —
 * convención del repo para que módulos de dominios RBAC distintos queden
 * independientes.
 */

import type { ReactNode } from 'react';
import { currentLocale } from '@/lib/locale';
import { todayUTC } from '@/../../shared/dates';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { WinclapLoader } from '@/components/ui/winclap-loader';

export const fmtNum = (n: number | null | undefined) =>
  n == null ? '—' : Math.round(n).toLocaleString('en-US');

export const fmtMs = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${Math.round(n)} ms`;

/** '2026-08-25' → 'Aug 25'. */
export const fmtDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
};

/** ISO de hora → '14:00' (hora local del navegador). */
export const fmtHour = (iso: string) =>
  new Date(iso).toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' });

/** ISO → 'Aug 25, 14:03' (hora local). */
export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(currentLocale(), {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

/** La telemetría se particiona por día UTC (internal-telemetry.ts): acá "hoy" es UTC a propósito. */
export const todayISO = todayUTC;


export function LoaderBlock() {
  return (
    <div className="flex items-center justify-center py-20">
      <WinclapLoader />
    </div>
  );
}

export function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <Card className="p-10 text-center">
      <p className="text-sm font-medium">{title}</p>
      {description && <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">{description}</p>}
    </Card>
  );
}

/** Pill de estado con semáforo (salud). */
export function StatusPill({ level, label }: { level: 'ok' | 'warn' | 'bad'; label: string }) {
  const cls = {
    ok: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
    warn: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
    bad: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400',
  }[level];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

/** Barra proporcional para rankings (estilo SkillsAnalytics). */
export function RatioBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
      <div className="h-full rounded-full bg-primary/70" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** La card de KPI es compartida (components/ui/stat-card); queda re-exportada por los imports viejos. */
export { StatCard } from '@/components/ui/stat-card';

// ── Tabla compacta de telemetría ──────────────────────────────────────────────
// 02/10/2026: las 9 tablas del panel repetían el mismo <table> a mano (reuse-scout en
// #444). Una sola, sobre las primitivas de `ui/table` con el padding compacto del panel.
// Vacío y loading se resuelven afuera (`EmptyState` / `LoaderBlock`); sin orden ni
// paginado porque ninguna lo usa (si hace falta ordenar: extraer el `useClientSort` que el
// catálogo propone a partir de las 3 copias de SortableHead, no un cuarto mecanismo).

export interface InternalColumn<T> {
  key: string;
  /** Ya traducido. */
  label: ReactNode;
  align?: 'left' | 'right';
  /** Clases de la celda: `font-mono text-xs`, `max-w-[220px] truncate`, `whitespace-nowrap`… */
  className?: string;
  /** `title` de la celda (el texto completo de algo truncado). */
  title?: (row: T) => string | undefined;
  render: (row: T, index: number) => ReactNode;
}

export function InternalTable<T>({ columns, rows, rowKey, className }: {
  columns: ReadonlyArray<InternalColumn<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T, index: number) => string;
  className?: string;
}) {
  const last = columns.length - 1;
  return (
    <Table className={cn('text-sm', className)}>
      <TableHeader className="text-xs text-muted-foreground">
        <TableRow className="hover:bg-transparent">
          {columns.map((c, i) => (
            <TableHead key={c.key} className={cn('h-auto px-0 py-2 pr-2 font-medium', c.align === 'right' && 'text-right', i === last && 'pr-0')}>
              {c.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={rowKey(row, i)}>
            {columns.map((c, j) => (
              <TableCell key={c.key} title={c.title?.(row)} className={cn('px-0 py-1.5 pr-2', c.align === 'right' && 'text-right', j === last && 'pr-0', c.className)}>
                {c.render(row, i)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
